import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpContext } from '@angular/common/http';
import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { SKIP_PENDING_REQUESTS } from '@app/api/interceptors/pending-requests.interceptor';
import { TranslateService } from '@ngx-translate/core';
import { mgcIconStarRegular } from '@sb/mgc-icons/mgcIcon-star_regular.icon';
import { featureFlag, FeatureService } from '@sb/shared/feature';
import { SbDialogService } from '@sb/ui/dialog';
import { IconRegistry } from '@sb/ui/icon';
import { catchError, EMPTY, filter, firstValueFrom, map, startWith, switchMap, timer, withLatestFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

interface VersionResponse {
  version: string;
}

const VERSION_POLL_INTERVAL_MS = 60000;
const VERSION_JSON_PATH = 'assets/config/app-version.json';
// 12 hours for session age threshold
const SESSION_AGE_THRESHOLD_MS = 12 * 60 * 60 * 1000;

@Injectable({
  providedIn: 'root',
})
export class VersionUpdateService {
  #http = inject(HttpClient);
  #router = inject(Router);
  #featureService = inject(FeatureService);
  #dialogService = inject(SbDialogService);
  #translate = inject(TranslateService);
  #destroyRef = inject(DestroyRef);
  #document = inject(DOCUMENT);

  #bootVersion = environment.version;
  #remoteVersion = signal<string | null>(this.#bootVersion);
  #pageLoadTimestamp = Date.now();
  #behindDetected = false;
  #promptShown = false;

  #isBehind = computed(() => {
    const remote = this.#remoteVersion();
    return remote !== null && remote !== this.#bootVersion;
  });

  constructor() {
    inject(IconRegistry).registerIcons([mgcIconStarRegular]);

    this.#startPolling();
    this.#attachRouterListener();
  }

  #startPolling(): void {
    timer(VERSION_POLL_INTERVAL_MS, VERSION_POLL_INTERVAL_MS)
      .pipe(
        filter(() => this.#isVersionPollingEnabled()),
        switchMap(() => {
          const url = `${VERSION_JSON_PATH}?ts=${Date.now()}`;
          return this.#http
            .get<VersionResponse>(url, {
              context: new HttpContext().set(SKIP_PENDING_REQUESTS, true),
            })
            .pipe(catchError(() => EMPTY));
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe((response) => {
        this.#handleVersionResponse(response.version);
      });
  }

  #attachRouterListener(): void {
    const initialUrl = this.#router.url;
    const initialTopLevelSegment = this.#getTopLevelSegment(initialUrl);

    const currentTopLevelSegment$ = this.#router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => this.#getTopLevelSegment(event.urlAfterRedirects)),
      startWith(initialTopLevelSegment),
    );

    this.#router.events
      .pipe(
        filter(() => this.#isVersionPollingEnabled()),
        filter((event): event is NavigationStart => event instanceof NavigationStart),
        withLatestFrom(currentTopLevelSegment$),
        filter(([event, currentTopLevelSegment]) => {
          const targetTopLevelSegment = this.#getTopLevelSegment(event.url);
          return this.#isBehind() && targetTopLevelSegment && targetTopLevelSegment !== currentTopLevelSegment;
        }),
      )
      .subscribe(([event]) => {
        this.#document.location.assign(event.url);
      });
  }

  #isVersionPollingEnabled(): boolean {
    return this.#featureService.isEnabled(featureFlag.WEBAPP_VERSION_POLLING);
  }

  #handleVersionResponse(remoteVersion: string): void {
    this.#remoteVersion.set(remoteVersion);

    const isBehind = this.#isBehind();

    if (isBehind && !this.#behindDetected) {
      this.#behindDetected = true;
      // TODO: restore tracking event when analytics contract is updated
      // void this.#trackingService.event('version_behind_detected', {
      //   boot_version: this.#bootVersion,
      //   remote_version: remoteVersion,
      //   session_age_ms: this.#sessionAgeMs(),
      // });
    }

    // dialog for long session idlers
    if (this.#shouldShowDialog()) {
      this.#openDialog(remoteVersion);
    }
  }

  #sessionAgeMs(): number {
    return Date.now() - this.#pageLoadTimestamp;
  }

  /**
   * Show the dialog if the version is behind and the session age is greater than 12 hours and the prompt has not been shown yet.
   */
  #shouldShowDialog(): boolean {
    return this.#isBehind() && this.#sessionAgeMs() >= SESSION_AGE_THRESHOLD_MS && !this.#promptShown;
  }

  async #openDialog(_remoteVersion: string) {
    this.#promptShown = true;
    // TODO: restore tracking event when analytics contract is updated
    // void this.#trackingService.event('version_update_prompt_shown', {
    //   boot_version: this.#bootVersion,
    //   remote_version: remoteVersion,
    //   session_age_ms: this.#sessionAgeMs(),
    // });

    const dialogRef = this.#dialogService.openConfirm({
      type: 'info',
      icon: 'star-regular',
      title: this.#translate.instant('A new version of Shiftbase is ready!'),
      description: this.#translate.instant('Click to refresh, and load the most advanced version of Shiftbase today!'),
      primary: {
        text: this.#translate.instant('Click to refresh'),
      },
      secondary: {
        text: this.#translate.instant('Maybe later'),
      },
    });

    const result = await firstValueFrom(dialogRef.closed);

    if (!result) {
      return;
    }

    if (result.confirmed) {
      this.#document.location.reload();

      // TODO: restore tracking event when analytics contract is updated
      // void this.#trackingService.event('version_update_refresh_clicked', {
      //   boot_version: this.#bootVersion,
      //   remote_version: remoteVersion,
      //   session_age_ms: this.#sessionAgeMs(),
      // });
    } else {
      // TODO: restore tracking event when analytics contract is updated
      // void this.#trackingService.event('version_update_prompt_dismissed', {
      //   boot_version: this.#bootVersion,
      //   remote_version: remoteVersion,
      //   session_age_ms: this.#sessionAgeMs(),
      // });
    }
  }

  #getTopLevelSegment(url: string): string | null {
    const withoutQuery = url.split('?')[0];
    const withoutLeadingSlash = withoutQuery.startsWith('/') ? withoutQuery.slice(1) : withoutQuery;
    const [segment] = withoutLeadingSlash.split('/');
    return segment || null;
  }
}
